// Cliente de la API de entregas.
import { api } from '@/services/api'
import type {
  DataResponse,
  Delivery,
  DeliveryStatus,
  PaginatedResponse,
  PlannedOrder,
} from '@/types'

export interface DeliveryFilters {
  status?: string          // uno o varios separados por coma
  warehouse?: string
  driver_id?: string
  scheduled_date?: string  // YYYY-MM-DD
  ship_date?: string       // fecha comprometida en la OV (YYYY-MM-DD)
  ecom?: boolean           // false = oculta el almacén ECOM (marketplaces)
  sample?: boolean         // false = oculta las muestras a asesores
  sap_open?: boolean       // true = solo las que SAP sigue teniendo abiertas
  year?: number            // año de la fecha efectiva
  since?: string           // ventana reciente (YYYY-MM-DD)
  until?: string           // rezago: más viejas que esta fecha
  q?: string
  page?: number
  size?: number
}

export async function listDeliveries(f: DeliveryFilters = {}): Promise<PaginatedResponse<Delivery>> {
  const { data } = await api.get<PaginatedResponse<Delivery>>('/deliveries', { params: f })
  return data
}

/** Tablero "qué sale mañana": pedidos comprometidos a esa fecha, agrupados por almacén. */
export async function listPlanned(date: string, warehouse?: string): Promise<PlannedOrder[]> {
  const { data } = await api.get<DataResponse<PlannedOrder[]>>('/deliveries/planned', {
    params: { date, ...(warehouse ? { warehouse } : {}) },
  })
  return data.data
}

export interface DeliverySummary {
  years: { year: number; count: number }[]
  total: number
  sap_open: number   // SAP las sigue teniendo abiertas
  sap_closed: number // ya facturadas en SAP (aquí se factura ANTES de cargar el camión)
  stale_days: number // antigüedad a partir de la cual cuenta como rezago
  stale_open: number // viejas Y aún abiertas en SAP = lo que nadie cerró nunca
  samples: number    // cuántas son muestras (las que quita la casilla)
}

/** Fecha local en YYYY-MM-DD (toISOString daría UTC y cambiaría de día por la tarde). */
export function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

/** Traduce la opción del selector de periodo a filtros de la API. */
export function periodFilters(period: string, staleDays: number): DeliveryFilters {
  if (!period) return {}
  if (period === 'rezago') return { until: isoDaysAgo(staleDays), sap_open: true }
  if (/^[0-9]{4}$/.test(period)) return { year: Number(period) }
  return { since: isoDaysAgo(Number(period)) }
}

/** Conteos por año y por estado en SAP, con los MISMOS filtros que el listado (menos el año). */
export async function getDeliverySummary(f: DeliveryFilters = {}): Promise<DeliverySummary> {
  const { data } = await api.get<DataResponse<DeliverySummary>>('/deliveries/summary', { params: f })
  return data.data
}

export async function getDelivery(id: string): Promise<Delivery> {
  const { data } = await api.get<DataResponse<Delivery>>(`/deliveries/${id}`)
  return data.data
}

export async function assignDelivery(
  id: string,
  payload: { driver_id: string; scheduled_date: string },
): Promise<Delivery> {
  const { data } = await api.post<DataResponse<Delivery>>(`/deliveries/${id}/assign`, payload)
  return data.data
}

export async function rescheduleDelivery(
  id: string,
  payload: { scheduled_date: string; reason?: string },
): Promise<Delivery> {
  const { data } = await api.post<DataResponse<Delivery>>(`/deliveries/${id}/reschedule`, payload)
  return data.data
}

export async function changeDeliveryStatus(
  id: string,
  payload: { status: DeliveryStatus; note?: string; latitude?: number; longitude?: number; accuracy?: number },
): Promise<Delivery> {
  const { data } = await api.patch<DataResponse<Delivery>>(`/deliveries/${id}/status`, payload)
  return data.data
}

export async function syncDeliveries(): Promise<{ fetched: number; upserted: number }> {
  const { data } = await api.post<DataResponse<{ fetched: number; upserted: number }>>('/deliveries/sync')
  return data.data
}

// ── Helpers de presentación ──
export const STATUS_LABEL: Record<DeliveryStatus, string> = {
  pendiente: 'Pendiente',
  asignada: 'Asignada',
  en_ruta: 'En ruta',
  en_sitio: 'En sitio',
  entregada: 'Entregada',
  no_entregada: 'No entregada',
  cancelada: 'Cancelada',
}

export const STATUS_CLASS: Record<DeliveryStatus, string> = {
  pendiente: 'bg-gray-100 text-gray-700',
  asignada: 'bg-blue-100 text-blue-800',
  en_ruta: 'bg-indigo-100 text-indigo-800',
  en_sitio: 'bg-amber-100 text-amber-900',
  entregada: 'bg-green-100 text-green-800',
  no_entregada: 'bg-red-100 text-red-800',
  cancelada: 'bg-gray-200 text-gray-600',
}

/** Importe en la divisa REAL del documento (el backend ya lo normaliza). */
export function formatMoney(amount: number | null, currency: string | null): string {
  if (amount == null) return '—'
  const cur = currency === 'MXP' ? 'MXN' : currency || 'MXN'
  try {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: cur }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency ?? ''}`
  }
}

export const WAREHOUSES = ['Cancún', 'CDMX', 'Cabos', 'CEDIS', 'ECOM', 'SHOW', 'Vallarta']
